/**
 * ImportAppointmentCard
 *
 * Compact appointment card showing patient name, time, duration, and type.
 */

import { Avatar, Box, Chip, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';

import type { AppointmentItem } from '../../../types';
import type { ImportAppointmentCardProps } from '../ImportReviewWizard.types';

// =============================================================================
// Helpers
// =============================================================================

function getInitials(appt: AppointmentItem): string {
  const first = appt.first_name?.charAt(0)?.toUpperCase() ?? '';
  const last = appt.last_name?.charAt(0)?.toUpperCase() ?? '';
  return first + last || '?';
}

function formatTimeRange(appt: AppointmentItem): string {
  const start = dayjs(appt.appointment_start_time);
  const startStr = start.format('h:mm A');

  if (appt.appointment_duration) {
    const end = start.add(appt.appointment_duration, 'minute');
    return `${startStr} \u2013 ${end.format('h:mm A')}`;
  }
  return startStr;
}

// =============================================================================
// Component
// =============================================================================

export const ImportAppointmentCard = ({ appointment }: ImportAppointmentCardProps) => {
  const fullName = [appointment.first_name, appointment.last_name].filter(Boolean).join(' ') || 'Unknown';
  const timeRange = formatTimeRange(appointment);
  const duration = appointment.appointment_duration ? `${appointment.appointment_duration} min` : null;
  const encounterType = appointment.appointment_encounter_type;
  const providerName = appointment.provider_name;
  const isNew = !appointment.match_status || appointment.match_status === 'unmatched';

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        '&:not(:last-child)': { borderBottom: 'none' },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: '0.85rem',
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
        }}
      >
        {getInitials(appointment)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {fullName}
          </Typography>
          <Chip
            label={isNew ? 'New Patient' : 'Existing Patient'}
            color={isNew ? 'info' : 'success'}
            variant="outlined"
            size="small"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {[timeRange, duration, encounterType, providerName].filter(Boolean).join('  \u00B7  ')}
        </Typography>
      </Box>
    </Paper>
  );
};
